// Cognira Offline Database — IndexedDB
// All ERP data stored locally, syncs when online

const DB_NAME = 'cognira_erp';
const DB_VERSION = 1;

const STORES = {
  // School
  students:     { keyPath: 'id', indexes: ['student_id', 'class_name', 'status'] },
  attendance:   { keyPath: 'id', indexes: ['student_id', 'date'] },
  fees:         { keyPath: 'id', indexes: ['student_id', 'status'] },
  exams:        { keyPath: 'id', indexes: ['class_name'] },
  results:      { keyPath: 'id', indexes: ['student_id', 'exam_id'] },
  // Hospital
  patients:     { keyPath: 'id', indexes: ['patient_id', 'phone'] },
  doctors:      { keyPath: 'id', indexes: ['specialization'] },
  appointments: { keyPath: 'id', indexes: ['patient_id', 'doctor_id', 'date'] },
  prescriptions:{ keyPath: 'id', indexes: ['patient_id'] },
  // POS / Restaurant
  products:     { keyPath: 'id', indexes: ['category', 'sku'] },
  orders:       { keyPath: 'id', indexes: ['status', 'date'] },
  order_items:  { keyPath: 'id', indexes: ['order_id'] },
  // Common
  sync_queue:   { keyPath: 'id', indexes: ['store', 'synced'] },
  settings:     { keyPath: 'key' },
};

let dbInstance = null;

export const openDB = () => {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      Object.entries(STORES).forEach(([name, config]) => {
        if (!db.objectStoreNames.contains(name)) {
          const store = db.createObjectStore(name, {
            keyPath: config.keyPath,
            autoIncrement: config.keyPath === 'id'
          });
          (config.indexes || []).forEach(idx => {
            store.createIndex(idx, idx, { unique: false });
          });
        }
      });
    };

    req.onsuccess = (e) => {
      dbInstance = e.target.result;
      resolve(dbInstance);
    };
    req.onerror = () => reject(req.error);
  });
};

// Generic CRUD operations
export const dbOps = {
  async add(storeName, data) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const item = { ...data, id: data.id || Date.now(), _created: new Date().toISOString(), _synced: false };
      const req = store.add(item);
      req.onsuccess = () => {
        resolve({ ...item, id: req.result });
        // Queue for sync
        dbOps.queueSync(storeName, 'add', item);
      };
      req.onerror = () => reject(req.error);
    });
  },

  async update(storeName, data) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const item = { ...data, _updated: new Date().toISOString(), _synced: false };
      const req = store.put(item);
      req.onsuccess = () => {
        resolve(item);
        dbOps.queueSync(storeName, 'update', item);
      };
      req.onerror = () => reject(req.error);
    });
  },

  async getAll(storeName, indexName, value) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      let req;
      if (indexName && value !== undefined) {
        const idx = store.index(indexName);
        req = idx.getAll(value);
      } else {
        req = store.getAll();
      }
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  },

  async get(storeName, id) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).get(id);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  },

  async delete(storeName, id) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const req = tx.objectStore(storeName).delete(id);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error);
    });
  },

  async count(storeName) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  },

  async queueSync(storeName, action, data) {
    try {
      const db = await openDB();
      const tx = db.transaction('sync_queue', 'readwrite');
      tx.objectStore('sync_queue').add({
        id: Date.now(),
        store: storeName,
        action,
        data,
        synced: false,
        queued_at: new Date().toISOString()
      });
    } catch(e) { /* ignore sync queue errors */ }
  },

  async getPendingSync() {
    return dbOps.getAll('sync_queue', 'synced', false);
  },

  async markSynced(id) {
    const item = await dbOps.get('sync_queue', id);
    if (item) await dbOps.update('sync_queue', { ...item, synced: true });
  }
};

// Sync pending items to server
export const syncToServer = async (apiBase) => {
  if (!navigator.onLine) return { synced: 0, pending: 0 };

  const pending = await dbOps.getPendingSync();
  let synced = 0;

  for (const item of pending) {
    try {
      const endpoint = {
        students:     '/school/student',
        attendance:   '/school/attendance/mark',
        fees:         '/school/fee/collect',
        exams:        '/school/exam',
        patients:     '/hospital/patient',
        appointments: '/hospital/appointment',
        orders:       '/pos/order',
      }[item.store];

      if (endpoint) {
        await fetch(`${apiBase}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.data)
        });
        await dbOps.markSynced(item.id);
        synced++;
      }
    } catch(e) { /* will retry next time */ }
  }

  return { synced, pending: pending.length - synced };
};

// Check connection status
export const isOnline = () => navigator.onLine;

// Listen for online/offline events
export const onConnectionChange = (callback) => {
  window.addEventListener('online',  () => callback(true));
  window.addEventListener('offline', () => callback(false));
};
